/**
 * Resolve the button indices emitted by openHASP for a btnmatrix.
 *
 * `options` is the persisted wire definition. Newline entries create rows but
 * are not buttons and therefore do not consume a firmware `val` index. All
 * other entries consume an index, including hidden/disabled entries, so a
 * selector never renumbers the firmware payload.
 */
export function getBtnmatrixButtons(source) {
  const options = Array.isArray(source)
    ? source
    : Array.isArray(source?.listOptions)
      ? source.listOptions.flatMap(row => Array.isArray(row) ? row : [row])
      : Array.isArray(source?.options)
        ? source.options
        : [];

  const buttons = [];
  for (const option of options) {
    if (isNewlineOption(option)) continue;

    const value = buttons.length;
    const text = optionText(option);
    const status = [
      option?.hidden ? 'hidden' : '',
      option?.disabled ? 'disabled' : '',
    ].filter(Boolean);
    const statusLabel = status.length > 0 ? ` (${status.join(', ')})` : '';
    buttons.push({
      value,
      text,
      hidden: option?.hidden === true,
      disabled: option?.disabled === true,
      label: `${text || `Button ${value}`} (${value})${statusLabel}`,
    });
  }
  return buttons;
}

export function btnmatrixFilterValue(trigger) {
  const value = trigger?.filter?.val;
  return value === undefined || value === null || value === '' ? null : value;
}

export function isValidBtnmatrixFilter(trigger) {
  const value = btnmatrixFilterValue(trigger);
  if (value === null) return true;
  return Number.isInteger(Number(value)) && Number(value) >= 0;
}

export function findBtnmatrixButton(source, value) {
  const index = Number(value);
  return getBtnmatrixButtons(source).find(button => button.value === index) ?? null;
}

/** Jinja expression used by both Home Assistant YAML exporters. */
export function btnmatrixFilterExpression(trigger) {
  const value = btnmatrixFilterValue(trigger);
  if (value === null || !Number.isInteger(Number(value))) return null;
  return `{{ val | int(-1) == ${Number(value)} }}`;
}

function isNewlineOption(option) {
  return option === '\n' || option?.newline === true || option?.type === 'newline';
}

function optionText(option) {
  if (typeof option === 'string') return option;
  if (option == null) return '';
  return String(option.text ?? option.label ?? option.name ?? option.value ?? '');
}
